import findKey from "lodash/findKey";
import { useTranslation } from "next-i18next";
import { useCallback } from "react";

import { uploadGoogleDoc } from "@/core/client/application/applicationGoogleDoc";

import {
  DocExtension,
  DocGenerator,
  docGeneratorMap,
  DocType,
  extensionsMap,
} from "./";

interface UseGenerateDocParams<T extends DocType> {
  type: T;
  name: string | null;
  googleDriveDir: string | null;
  meta?: Record<string, any>;
  data: Parameters<typeof docGeneratorMap[T]>[1];
}

function useGenerateDoc<T extends DocType>({
  type,
  name,
  googleDriveDir,
  data,
  meta,
}: UseGenerateDocParams<T>) {
  const { i18n } = useTranslation();

  // Use fixed `t` to genrate doc in English even if the user is using another language
  const t = i18n.getFixedT("en", ["application"]);

  const docGenerator = docGeneratorMap[type] as DocGenerator<
    Parameters<typeof docGeneratorMap[T]>[1]
  >;

  return useCallback(
    async () => {
      const fileExtension = findKey(extensionsMap, (docTypes) =>
        docTypes.includes(type)
      ) as DocExtension;

      try {
        const blob = await docGenerator(t, data);

        return await uploadGoogleDoc({
          googleDriveDir,
          applicationId: data.application.id,
          blob,
          type,
          fileName: name,
          fileExtension,
          meta,
        });
      } catch (err) {
        console.warn("Failed to generate doc", err);
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      t,
      docGenerator,
      type,
      name,
      meta,
      googleDriveDir,
      // eslint-disable-next-line react-hooks/exhaustive-deps
      ...Object.values(data),
    ]
  );
}

export { useGenerateDoc };
